import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import { moduleConfig } from './config';

export interface SpareCategory {
  id: number;
  libraryId: number;
  pid?: number | null;
  name: string;
  abrv?: string | null;
  sortOrder?: number | null;
  level?: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const useSpareCategories = (
  searchParams?: string,
  page = 1,
  limit = 25,
  libraryId?: number | null,
) => {
  const libQ = libraryId != null && Number.isFinite(libraryId) ? `&libraryId=${libraryId}` : '';
  const q = `?page=${page}&limit=${limit}${searchParams ? `&searchParams=${encodeURIComponent(searchParams)}` : ''}${libQ}`;
  const { data, error, isLoading, mutate } = useSWR(
    `${moduleConfig.module.api.list}${q}`,
    fetcher,
    { revalidateOnFocus: true },
  );
  const rows = Array.isArray(data) ? data : (data?.data ?? []);
  const total = Array.isArray(data) ? data.length : (data?.total ?? rows.length);
  return { data: rows as SpareCategory[], total, page, limit, isLoading, isError: error, mutate };
};

export const useSpareCategory = (id?: number) => {
  const { data, error, isLoading, mutate } = useSWR<SpareCategory>(
    id != null && id > 0 ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data, item: data, isLoading, isError: error, mutate };
};

export const useSpareCategoryTree = (libraryId?: number | null) => {
  const { data, error, isLoading, mutate } = useSWR<SpareCategory[]>(
    libraryId != null && libraryId > 0 ? moduleConfig.module.api.tree(libraryId) : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data: data ?? [], isLoading, isError: error, mutate };
};

export type SpareCategoryTreeNode = SpareCategory & { children: SpareCategoryTreeNode[] };

export function buildSpareCategoryTree(flat: SpareCategory[]): SpareCategoryTreeNode[] {
  const byId = new Map<number, SpareCategoryTreeNode>();
  for (const row of flat) {
    byId.set(row.id, { ...row, children: [] });
  }
  const roots: SpareCategoryTreeNode[] = [];
  for (const node of Array.from(byId.values())) {
    if (node.pid != null && byId.has(node.pid)) {
      byId.get(node.pid)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  const sortNodes = (nodes: SpareCategoryTreeNode[]) => {
    nodes.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));
    nodes.forEach((n) => sortNodes(n.children));
  };
  sortNodes(roots);
  return roots;
}
